<template>
  <div class="grid w-full grid-cols-2">
    <!--  color select  -->
    <div class="col-span-2 w-full md:order-5 md:mt-8">
      <SFSiblingSelection :product="product" />
    </div>
    <!--  promotions/discounts NOTE: can be some other component  -->
    <div class="col-span-2 w-full px-4 md:order-3 md:col-span-1 md:px-0">
      <div
        v-if="relativeReductions.length"
        class="mb-2 mt-[22px] flex w-full flex-wrap space-x-2 md:mb-0"
      >
        <span
          v-for="({ value, category }, index) in relativeReductions"
          :key="`${value}-badge-${category}-${index}`"
          class="w-fit rounded-lg bg-red-700 p-1 text-sm font-semibold text-white"
        >
          -{{ value }}%
        </span>
      </div>
    </div>
    <!--  title Section  -->
    <div class="col-span-2 w-full px-4 md:order-2 md:mt-12 md:px-0">
      <h1 class="w-full text-lg text-gray-600">
        <SFLocalizedLink
          v-if="currentBrandURL"
          :to="currentBrandURL"
          class="!text-lg !font-normal !uppercase !leading-6 hover:text-orange-500 md:!text-[1.5rem] md:!leading-7"
          data-testid="pdp-product-brand"
        >
          {{ brand }}
        </SFLocalizedLink>
        <span
          v-else
          class="!text-lg !font-normal !uppercase !leading-6 md:!text-[1.5rem] md:!leading-7"
          :title="brand"
          data-testid="pdp-product-brand"
        >
          {{ brand }}
        </span>
        <SFHeadline size="lg" class="md:mt-4" data-testid="pdp-product-name">
          <span
            class="break-words text-[1.5rem] font-bold leading-[1.8rem] tracking-[-0.09rem] text-anthracite-800"
            :title="name"
          >
            {{ name }}
          </span>
        </SFHeadline>
      </h1>
    </div>
    <!--  price/shipping section   -->
    <div
      class="col-span-2 mb-5 mt-3 grid w-full grid-cols-1 px-4 md:order-4 md:row-start-4 md:px-0"
    >
      <div>
        <SFProductPrice
          v-if="price"
          size="lg"
          :price="price"
          :show-badges="false"
          :promotion="promotion"
          :lowest-prior-price="lowestPriorPrice"
          type="normal"
          show-tax-info
          :show-price-from="showFrom"
          :coins="true"
        />
      </div>
    </div>
    <!--  actions  -->
    <div class="col-span-2 w-full px-4 md:order-6 md:mt-8 md:px-0">
      <SFProductActions
        :product="product"
        :promotion="promotion ?? undefined"
      />
    </div>
    <!--  info with icons   -->
    <div
      class="col-span-2 w-full space-y-3 bg-gray-200 p-4 text-sm text-orange-400 md:order-7 md:rounded-lg"
    >
      <div class="flex space-x-2">
        <IconReturn class="size-5" />
        <span class="text-gray-700">{{
          $t('global.product.free_returns')
        }}</span>
      </div>
      <div class="flex space-x-2">
        <IconCap class="size-5" />
        <span class="text-gray-700">{{
          $t('global.product.fast_shipping')
        }}</span>
      </div>
      <div class="flex space-x-2">
        <IconBox class="size-5" />
        <span class="text-gray-700">{{
          $t('global.product.free_shipping')
        }}</span>
      </div>
    </div>
    <!-- descriptions   -->
    <div class="col-span-2 w-full px-4 md:order-7 md:hidden md:px-0">
      <SFProductDetails :product="product" />
    </div>
    <!--  breadcrumbs  -->
    <div class="col-span-2 w-full px-4 md:order-1 md:px-0">
      <SFProductBreadcrumbs
        v-if="longestCategoryList"
        class="my-2 md:mb-0"
        :product-categories="longestCategoryList"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  Price,
  Product,
  Promotion,
  Variant,
} from '@scayle/storefront-nuxt'
import { getFirstAttributeValue, slugify } from '@scayle/storefront-nuxt'
import { computed, watch } from 'vue'
import { useBasket, useProductPrice } from '#storefront/composables'
import SFSiblingSelection from '~/components/product/SFSiblingSelection.vue'
import { SFHeadline } from '#storefront-ui/components'
import SFLocalizedLink from '~/components/SFLocalizedLink.vue'
import SFProductPrice from '~/components/product/SFProductPrice.vue'
import SFProductBreadcrumbs from '~/components/product/SFProductBreadcrumbs.vue'
import SFProductDetails from '~/components/product/SFProductDetails.vue'
import SFProductActions from '~/components/product/detail/SFProductActions.vue'
import { useRoute, useRouter } from '#app/composables/router'
import { useProductBaseInfo } from '~/composables'
import { hasSubscriptionCustomData } from '~/modules/subscription/helpers/subscription'
import { useRouteHelpers } from '~/composables/useRouteHelpers'
import useSizeSelector from '~/composables/useSizeSelector'
import { useShopGenderCookie } from '~/composables/useShopGenderCookie'

const { product } = defineProps<{
  product: Product
  name: string
  brand: string
  promotion?: Promotion
}>()

const { items: basketItems } = useBasket()
const route = useRoute()
const router = useRouter()
const { activeVariant, setActiveVariant, setProduct } = useSizeSelector()
const { getProductDetailRoute, buildSingleBrandPath } = useRouteHelpers()

const { cookie: currentGender } = useShopGenderCookie()

const currentBrandCategory = computed(() => {
  const _categories = product.categories?.flat()
  return _categories?.find(
    (category) =>
      category.categoryUrl.startsWith(
        `/${currentGender.value.toLowerCase()}`,
      ) &&
      category.categoryUrl.includes('brand') &&
      category.categoryUrl.includes(slugify(brand.value)),
  )
})

const currentBrandURL = computed(() => {
  if (!currentBrandCategory.value) {
    return ''
  }
  return buildSingleBrandPath({
    id: currentBrandCategory.value?.categoryId,
    path: currentBrandCategory.value?.categoryUrl,
  })
})

const updateActiveVariant = async (newVariant?: Variant) => {
  const routeIsAlreadyUpdated =
    newVariant?.id && route.path.includes(newVariant.id.toString())

  if (!newVariant || routeIsAlreadyUpdated) {
    return
  }

  const productName = getFirstAttributeValue(product.attributes, 'name')?.label
  const productColor = getFirstAttributeValue(product.attributes, 'color')
    ?.label

  const newPath = getProductDetailRoute(
    product.id,
    productName,
    productColor,
    brand.value,
    newVariant.id,
  )

  await router.replace({
    path: newPath,
    query: route.query,
    hash: route.hash,
  })
}

watch(
  () => activeVariant.value,
  (newVariant) => updateActiveVariant(newVariant),
)

const { name, brand, longestCategoryList, variants } = useProductBaseInfo(
  () => product,
)

const basketItem = computed(() => {
  return basketItems.value?.find(
    ({ variant, customData }) =>
      variant.id === activeVariant.value?.id &&
      !hasSubscriptionCustomData(customData as Record<string, unknown>),
  )
})

const price = computed(() => {
  if (basketItem.value) {
    return basketItem.value.price.unit as Price
  } else if (activeVariant.value) {
    return activeVariant.value.price
  }
  return product.priceRange?.min as Price
})

const { relativeReductions } = useProductPrice(() => price.value)

const lowestPriorPrice = computed(() => {
  if (basketItem.value) {
    return basketItem.value.lowestPriorPrice
  } else if (activeVariant.value) {
    return activeVariant.value.lowestPriorPrice
  }
  return product.lowestPriorPrice
})
const showFrom = computed(
  () =>
    !activeVariant.value &&
    product.priceRange?.min.withTax !== product.priceRange?.max.withTax,
)

const variantIdPathParam = computed(() => {
  const slug = Array.isArray(route.params.slug)
    ? route.params.slug
    : [route.params.slug]

  if (slug.length > 1) {
    const variantSegment = slug[1]

    const variantId = /^\d+$/.test(variantSegment)
      ? parseInt(variantSegment)
      : undefined
    if (variantId) {
      const variant = variants.value.find((variant) => variant.id === variantId)
      setProduct(product)
      setActiveVariant(variant)
      return variantId
    }
    return undefined
  }
  return undefined
})

watch(
  () => variantIdPathParam.value,
  () => {
    const variant = variants.value.find(
      (variant) => variant.id === variantIdPathParam.value,
    )
    if (variant) {
      setProduct(product)
      setActiveVariant(variant)
    }
  },
)
</script>

<style scoped></style>
